/**
 * useGuideCatalog — shared DB-driven catalog of selectable travel guides for
 * the agent-switcher, backed by GET /functions/v1/chat/guides (see
 * `@wg/api` `getGuides`). Returns every active agent with coverage: location
 * guides (`kind: 'geo'`) and experience overlays (`kind: 'overlay'`). New geos
 * added to the backend auto-appear here with no client release.
 *
 * Lives in @wg/hooks so RN and wunderguide-web consume one implementation
 * (each injects its configured axios client). Fully optional/additive: on any
 * error it returns an empty list so the selector degrades to its existing
 * sessions + search flow.
 */
import { useCallback, useEffect, useState } from 'react';
import type { AxiosInstance } from 'axios';
import { getGuides, type Guide } from '@wg/api';

export type { Guide };

export function useGuideCatalog(http: AxiosInstance, enabled = true) {
  const [guides, setGuides] = useState<Guide[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchGuides = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setGuides(await getGuides(http));
    } catch (err) {
      setError(
        err && typeof err === 'object' && 'message' in err
          ? (err as Error).message
          : 'Failed to load guides',
      );
      setGuides([]);
    } finally {
      setLoading(false);
    }
  }, [http]);

  useEffect(() => {
    if (enabled) fetchGuides().catch(() => {});
  }, [enabled, fetchGuides]);

  return { guides, loading, error, refetch: fetchGuides };
}

export default useGuideCatalog;
